/**
 * Performance Trends contract — one flat router, `performanceTrendsContract`
 * (`/performance/trends`), two GETs. See the Performance module plan's Phase 2 for why this is a
 * new endpoint rather than a reuse of an existing one:
 *
 * - `listCheckIns` (goal.contract.ts) is per-goal only (`/:goalId/check-ins`) — a person-wide
 *   momentum series across every goal would be N requests, one per goal.
 * - `listRecognition`/`listFeedback` both order by `id: 'asc'`, not chronologically, and expose no
 *   date filters or totals — bucketing them by month client-side would mean paging an entire
 *   history first.
 * - `getPointsBalance` (recognition.contract.ts) returns only the current effective-dated row, not
 *   the history behind it.
 *
 * So: one new router, backed by SQL-side aggregation (`groupBy`, precedent at
 * `routes/recognition.ts:531`) rather than `findMany` + reduce. This file is CONTRACT ONLY — no
 * route implementation lands with it.
 *
 * NO "MY RANK" (decision D4, performance remediation; PRF-24, N29). `getMyRank` was removed: like
 * Keka, Darwinbox, Zoho People and Lattice, an employee never sees an individual performance rank or
 * percentile. Recognition keeps its public top-N `getLeaderboard` and the caller's own points
 * balance. `performance-no-rank.contract.test.ts` fails if an employee-facing performance route ever
 * returns a rank/percentile field again.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { goalConfidenceSchema } from './goal.contract.js';
import { errorBodySchema, instantSchema, percentSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// getGoalMomentum
// ---------------------------------------------------------------------------

/** One check-in, reduced to what a momentum sparkline/line needs — not the full `goalCheckInSchema`
 *  (no author, no note, no source): this is a trend shape, not a check-in detail view. */
export const goalMomentumPointSchema = z.object({
  occurredAt: instantSchema,
  progressPercent: percentSchema,
  confidence: goalConfidenceSchema,
});

/** One goal's check-in time series, oldest → newest. */
export const goalMomentumSeriesSchema = z.object({
  goalId: uuidSchema,
  goalTitle: z.string(),
  points: z.array(goalMomentumPointSchema),
});
export type GoalMomentumSeries = z.infer<typeof goalMomentumSeriesSchema>;

export const getGoalMomentumQuerySchema = z.object({
  /** Defaults to the caller; a manager may pass a visible report's id, same gating as the
   *  dashboard's `getOverview`. */
  personId: uuidSchema.optional(),
  /** Caps the number of goals returned (most recently active first), not the number of check-ins
   *  within a goal — a momentum panel shows a handful of sparklines, not every goal ever opened. */
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

// ---------------------------------------------------------------------------
// getActivityTrend
// ---------------------------------------------------------------------------

/** One calendar-month bucket of recognition/feedback activity. Counts only — no PII, no message
 *  bodies; a trend chart never needs either. */
export const activityTrendBucketSchema = z.object({
  /** e.g. "2026-03" rendered as "Mar 2026" client-side — sortable as a string, unlike a bare
   *  month name. */
  monthLabel: z.string(),
  praiseCount: z.number().int().nonnegative(),
  constructiveCount: z.number().int().nonnegative(),
  recognitionReceivedCount: z.number().int().nonnegative(),
  recognitionSentPoints: z.number().int().nonnegative(),
});
export type ActivityTrendBucket = z.infer<typeof activityTrendBucketSchema>;

export const getActivityTrendQuerySchema = z.object({
  personId: uuidSchema.optional(),
  /** Trailing window width, oldest → newest, ending at the current month. */
  months: z.coerce.number().int().min(1).max(24).default(6),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const performanceTrendsContract = c.router(
  {
    getGoalMomentum: {
      method: 'GET',
      path: '/goal-momentum',
      query: getGoalMomentumQuerySchema,
      responses: {
        200: z.array(goalMomentumSeriesSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Per-goal check-in time series (progress + confidence over time) for the caller or a visible report',
      metadata: routeMeta('goal:read'),
    },

    getActivityTrend: {
      method: 'GET',
      path: '/activity',
      query: getActivityTrendQuerySchema,
      responses: {
        200: z.array(activityTrendBucketSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Recognition + feedback activity bucketed by month for the caller or a visible report',
      metadata: routeMeta(['feedback:read', 'recognition:give']),
    },
  },
  { pathPrefix: '/performance/trends' },
);
